import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Wallet, Plus, Trash2, ChevronDown, Save, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import { useAuth } from "@/context/AuthContext";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function ProfitLoss({ alwaysOpen = false }) {
  const { businessId, selectedVenue, venues, monthDate } = useDash();
  const { user, business } = useAuth();
  const qc = useQueryClient();
  const activeVenue = selectedVenue || venues[0];
  const [open, setOpen] = useState(alwaysOpen);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;

  const isEmployee = user?.role === "employee";
  const empBlocked = isEmployee && !business?.employee_expense_access;

  const { data, isLoading } = useQuery({
    queryKey: ["expenses", businessId, activeVenue?.venue_id, monthKey],
    queryFn: async () =>
      (await api.get("/expenses", { params: { business_id: businessId, venue_id: activeVenue?.venue_id, month: monthKey } })).data,
    enabled: open && !!businessId && !!activeVenue && !empBlocked,
  });

  useEffect(() => { if (data) setRows(data.items.map((it) => ({ ...it }))); }, [data]);

  const revenue = data?.revenue || 0;
  const canEdit = !!data?.can_edit;
  const totalExp = useMemo(() => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0), [rows]);
  const profit = revenue - totalExp;

  const updateRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows([...rows, { label: "", amount: 0 }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(
        "/expenses",
        { venue_id: activeVenue.venue_id, month: monthKey, items: rows.filter((r) => r.label.trim()).map((r) => ({ label: r.label.trim(), amount: Number(r.amount) || 0 })) },
        { params: { business_id: businessId } }
      );
      toast.success("Profit & Loss saved");
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["analytics"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  if (empBlocked) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-pulse-sm" data-testid="profit-loss">
      <button onClick={() => !alwaysOpen && setOpen(!open)} data-testid="pnl-toggle"
        className={`flex w-full items-center justify-between p-5 text-left transition-colors ${alwaysOpen ? "cursor-default" : "hover:bg-cloud/40"}`}>
        <div className="flex items-center gap-2.5">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-pulse-navy"><Wallet size={17} className="text-pulse-green" /></div>
          <div>
            <h2 className="text-lg font-semibold text-pulse-navy">Profit &amp; Loss <span className="text-sm font-normal text-slate">· optional</span></h2>
            <p className="text-xs text-slate">Track expenses for {activeVenue?.name} and see your real monthly profit.</p>
          </div>
        </div>
        {!alwaysOpen && <ChevronDown size={20} className={`shrink-0 text-slate transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-5" data-testid="pnl-body">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-medium text-charcoal">{MONTHS[month]} {year} · {activeVenue?.name}</span>
            {data?.carried && !data?.saved && (
              <span className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">Carried from last month — edit &amp; save</span>
            )}
          </div>

          {isLoading ? (
            <div className="py-8 text-center text-sm text-slate">Loading…</div>
          ) : (
            <>
              <div className="space-y-2">
                {rows.map((r, i) => (
                  <div key={i} className="flex flex-col gap-2 rounded-md border border-slate-100 p-2 sm:flex-row sm:items-center sm:border-0 sm:p-0" data-testid={`pnl-row-${i}`}>
                    <input value={r.label} onChange={(e) => updateRow(i, { label: e.target.value })} disabled={!canEdit}
                      placeholder="Expense name"
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-base outline-none focus:border-pulse-green disabled:bg-cloud/50 disabled:text-slate sm:flex-1 sm:text-sm" />
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1 sm:w-40 sm:flex-none">
                        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate">₹</span>
                        <input type="number" min="0" value={r.amount} onChange={(e) => updateRow(i, { amount: e.target.value })} disabled={!canEdit}
                          className="w-full rounded-md border border-slate-200 py-2 pl-6 pr-2 text-base outline-none focus:border-pulse-green disabled:bg-cloud/50 disabled:text-slate sm:text-sm" />
                      </div>
                      {canEdit && (
                        <button onClick={() => removeRow(i)} data-testid={`pnl-remove-${i}`}
                          className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-slate-200 text-slate transition-colors hover:bg-red-50 hover:text-red-600">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {canEdit && (
                <button onClick={addRow} data-testid="pnl-add-row"
                  className="mt-3 flex items-center gap-1.5 rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate transition-colors hover:border-pulse-green hover:text-pulse-navy">
                  <Plus size={15} /> Add expense
                </button>
              )}

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <div className="rounded-md bg-cloud px-4 py-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate">Revenue</div>
                  <div className="mt-1 text-xl font-bold text-pulse-navy" data-testid="pnl-revenue">{formatINR(revenue)}</div>
                </div>
                <div className="rounded-md bg-cloud px-4 py-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate">Expenses</div>
                  <div className="mt-1 text-xl font-bold text-red-600" data-testid="pnl-expenses">{formatINR(totalExp)}</div>
                </div>
                <div className={`rounded-md px-4 py-3 ${profit >= 0 ? "bg-pulse-green/10" : "bg-red-50"}`}>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate">Profit this month</div>
                  <div className={`mt-1 text-xl font-bold ${profit >= 0 ? "text-pulse-green" : "text-red-600"}`} data-testid="pnl-profit">{formatINR(profit)}</div>
                </div>
              </div>

              {canEdit ? (
                <button onClick={save} disabled={saving} data-testid="pnl-save"
                  className="mt-4 flex items-center justify-center gap-2 rounded-md bg-pulse-navy px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60">
                  <Save size={16} /> {saving ? "Saving…" : "Save Profit & Loss"}
                </button>
              ) : (
                <p className="mt-4 flex items-center gap-2 text-xs text-slate"><Lock size={13} /> Only the owner can edit expenses. Ask them to enable your access in Settings.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
